import React, { useEffect, useMemo, useRef, useState } from 'react';

import { orderBy } from 'lodash';
import { generatePath, useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { useBasePath } from 'hooks/useBasePath';
import { useFullStoryPageView } from 'hooks/useFullstoryPageView';

import { useDashboardStore } from 'store/dashboard.store';

import { cardsDataQueryKey } from 'api/queries/cardsData.query';
import { useQueryGetDashboards } from 'api/queries/getDashboards.query';
import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { MainCardRoutingModal } from 'components/common/MainCardRoutingModal/mainCardRoutingModal';
import { HeaderWrapper } from 'components/common/PageContainer/HeaderWrapper';
import { MainContent } from 'components/common/PageContainer/MainContent';
import PageContainer from 'components/common/PageContainer/PageContainer';

import { PinboardWidgetWrapper } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/PinboardWidgetWrapper';
import { TopHeader } from 'components/Dashboards/Project/Components/CustomDashboard/TopHeader/topHeader';
import { getCurrentDashboardId } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { NoDataCustomDashboard } from 'components/Dashboards/Project/Components/CustomDashboard/noDataCustomDashboard';
import {
    IChildRefs,
    IDashboard,
    ISubTitleForExport,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';

/**
 * enable users to collect their favorite Foresight widgets/components into a custom dashboard, shared with the whole team.
 * Users can create multiple Pinboards, name them, edit them and re-organize their widgets if needed.
 * A user defined narrative text can be added to any component added to the Pinboard.
 * @returns  "Pinboards" Custom Dashboard  JSX Component
 */
export const PinboardsPage = () => {
    const { dashboardId } = useParams<{ dashboardId?: string }>();
    const { dashboard: selectedDashboardId } = useDashboardStore((store) => store.selectedDashboard);
    const { data: projectHierarchyList, isLoading: isLoadingGeneralList } =
        useQueryGetProgramGeneralHierarchyList('Dashboards');
    const { data, isLoading } = useQueryGetDashboards();
    const dashboards: IDashboard[] = data?.dashboardDetails || [];
    const { setSelectedDashboard } = useDashboardStore();
    const history = useHistory();
    const { path } = useRouteMatch();
    const basePath = useBasePath();
    const dashboard: IDashboard | undefined = getCurrentDashboardId({ dashboards, dashboardId, selectedDashboardId });
    const childRefs = useRef<IChildRefs[]>([]);
    const [subTitleForExport, setSubTitleForExport] = useState<ISubTitleForExport>({} as ISubTitleForExport);

    useFullStoryPageView({ pageName: executionConfig.pinboards.title });

    useEffect(() => {
        dashboard && selectDashboard(dashboard.id);
        return () => {
            childRefs.current = [];
        };
    }, [dashboard]);

    const selectDashboard = (dashboardId) => {
        dashboardId
            ? history.push({ pathname: generatePath(path, { dashboardId }), search: location.search })
            : history.push(basePath);

        dashboardId &&
            setSelectedDashboard({
                dashboard: dashboardId,
            });
    };

    const addChildRefs = (el: HTMLDivElement | null, index: number) => {
        const childRefIndex = childRefs.current.findIndex((e) => e.el?.id === el?.id);
        if (el) {
            const id = el.id.replace('pinboard-component-', ''); //TODO: put 'pinboard-component-' in a constant to use in other places
            const item: IChildRefs = { el, order: index, id, title: '', subTitle: '' };
            childRefIndex < 0 ? childRefs.current.push(item) : childRefs.current.splice(childRefIndex, 1, item);
        }
    };

    const handleSubTitleForExport = ({
        widgetId,
        subTitle,
        title,
    }: {
        widgetId?: string;
        subTitle: string;
        title: string;
    }) => {
        widgetId && setSubTitleForExport((prev) => ({ ...prev, [widgetId]: { subTitle, title } }));
    };

    const sortedWidgets = useMemo(() => {
        return dashboard?.widgets ? orderBy(dashboard?.widgets, 'sequence', 'asc') : [];
    }, [dashboard]);
    return (
        <PageContainer>
            {!isLoading && !isLoadingGeneralList && (
                <>
                    <HeaderWrapper>
                        <TopHeader
                            pageTitle={executionConfig.pinboards.title}
                            dashboards={dashboards}
                            selectDashboard={selectDashboard}
                            dashboard={dashboard}
                            info={data?.info}
                            childRefs={childRefs}
                            subTitleForExport={subTitleForExport}
                        />
                    </HeaderWrapper>
                    <MainContent>
                        {sortedWidgets.map((widget, index) => {
                            return (
                                <PinboardWidgetWrapper
                                    key={`widget-wrapper-${widget.id}`}
                                    widget={widget}
                                    projectHierarchyList={projectHierarchyList}
                                    dashboardId={dashboard?.id}
                                    dashboard={dashboard}
                                    ref={(el) => addChildRefs(el, index)}
                                    setSubTitleForExport={handleSubTitleForExport}
                                />
                            );
                        })}
                        <NoDataCustomDashboard dashboard={dashboard} />
                    </MainContent>
                </>
            )}
            <MainCardRoutingModal
                queryKeys={[
                    'portfolioTrackedActivities',
                    'portfolioTrackedActivitiesBySearchWord',
                    'topOverdue',
                    'useQueryPrioritiesMatrixCards',
                    'useQueryGetMatrixData',
                    'recomendationTableData',
                    'keyAchievementsDataTable',
                    'summaryTrackedActivitiesTableData',
                    'versionCompareTrackedActivitieTableData',
                    'finishDateChangesTableData',
                    'startDateChangesTableData',
                    'statusChangesTableData',
                    'largestAddedActivitiesTableData',
                    'largestDeletedActivitiesTableData',
                    'criticalPathAddedTableData',
                    'criticalPathRemovedTableData',
                    'largestFloatChangesTableData',
                    'largestDurationVarianceTableData',
                    'relationshipTypeChangesTableData',
                    'relationshipCountChangesTableData',
                    'activityPrediction',
                    cardsDataQueryKey,
                    'getStoredTags',
                ]}
            />
        </PageContainer>
    );
};
